"use client"

import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { sliderVariants } from "@/lib/slider-variants"

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> &
    VariantProps<typeof sliderVariants> & {
      showValue?: boolean
    }
>(
  (
    {
      className,
      variant,
      size,
      showValue,
      defaultValue,
      value,
      min = 0,
      max = 100,
      ...props
    },
    ref
  ) => {
    const _values = React.useMemo(
      () =>
        Array.isArray(value)
          ? value
          : Array.isArray(defaultValue)
            ? defaultValue
            : [min, max],
      [value, defaultValue, min, max]
    )

    return (
      <SliderPrimitive.Root
        ref={ref}
        data-slot="slider"
        defaultValue={defaultValue}
        value={value}
        min={min}
        max={max}
        className={cn(
          sliderVariants({ variant, size, showValue }),
          className
        )}
        {...props}
      >
        <SliderPrimitive.Track
          data-slot="slider-track"
          className="wl-slider-track"
        >
          <SliderPrimitive.Range
            data-slot="slider-range"
            className="wl-slider-range"
          />
        </SliderPrimitive.Track>
        {Array.from({ length: _values.length }, (_, index) => (
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            key={index}
            className="wl-slider-thumb"
            data-value={_values[index]}
          />
        ))}
      </SliderPrimitive.Root>
    )
  }
)

Slider.displayName = "Slider"

export { Slider, sliderVariants }
